/**
 * Release Hub Block
 *
 * Provides a tabbed interface for releases, feedback, and roadmap.
 * Supports per-block customization of tab labels, layout, and content settings.
 */
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	Spinner,
	ToggleControl,
	TextControl,
	RangeControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import './editor.scss';

/**
 * Get admin URL for a given page slug.
 *
 * Uses the localized chaReleaseHubAdmin data if available,
 * falls back to constructing the URL from wpApiSettings.
 *
 * @param {string} page Admin page slug (e.g., 'changeloger-releases').
 * @return {string} Full admin URL.
 */
function getAdminUrl( page ) {
	if ( window.chaReleaseHubAdmin?.adminUrl ) {
		return window.chaReleaseHubAdmin.adminUrl + 'admin.php?page=' + page;
	}

	// Fallback: construct from wpApiSettings root URL.
	const root = window.wpApiSettings?.root || '/wp-json/';
	const base = root.replace( /wp-json\/?$/, '' );
	return base + 'wp-admin/admin.php?page=' + page;
}

/**
 * Tab panel notice component.
 *
 * Displays an informational notice inside each editor tab panel
 * with an icon, description, and a link to the relevant admin page.
 *
 * @param {Object} props            Component props.
 * @param {string} props.tab        Tab key identifier.
 * @param {string} props.label      Display label for the tab.
 * @param {string} props.adminPage  Admin page slug to link to.
 * @param {string} props.icon       SVG path data for the notice icon.
 * @param {string} props.message    Descriptive help message.
 * @param {string} props.linkText   Text for the admin link button.
 * @return {JSX.Element} The notice component.
 */
function TabPanelNotice( { tab, label, adminPage, icon, message, linkText } ) {
	return (
		<div className="cha-hub-tab-notice">
			<div className="cha-hub-tab-notice__icon">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="currentColor"
					width="28"
					height="28"
				>
					<path d={ icon } />
				</svg>
			</div>
			<div className="cha-hub-tab-notice__content">
				<strong>{ label }</strong>
				<p>{ message }</p>
			</div>
			<a
				href={ getAdminUrl( adminPage ) }
				className="cha-hub-tab-notice__link"
				target="_blank"
				rel="noopener noreferrer"
			>
				{ linkText }
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="currentColor"
					width="14"
					height="14"
				>
					<path
						fillRule="evenodd"
						d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5zm7.25-.182a.75.75 0 01.537-.218H16.5a.75.75 0 01.75.75v4.432a.75.75 0 01-1.5 0V7.56l-6.22 6.22a.75.75 0 11-1.06-1.06l6.22-6.22h-2.69a.75.75 0 01-.75-.75z"
						clipRule="evenodd"
					/>
				</svg>
			</a>
		</div>
	);
}

/**
 * Tab notice configuration for each tab.
 */
const TAB_CONFIG = {
	releases: {
		adminPage: 'changeloger-releases',
		icon: 'M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z',
		message: __( 'Releases will be displayed here on the frontend. Add, edit, and organize your product releases from the admin panel.', 'changeloger' ),
		linkText: __( 'Manage Releases', 'changeloger' ),
	},
	feedback: {
		adminPage: 'changeloger-feedback',
		icon: 'M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z',
		message: __( 'The Feedback board will be displayed here on the frontend. Collect and manage user feedback from the admin panel.', 'changeloger' ),
		linkText: __( 'Manage Feedback', 'changeloger' ),
	},
	roadmap: {
		adminPage: 'changeloger-roadmap',
		icon: 'M11.47 1.72a.75.75 0 011.06 0l3 3a.75.75 0 01-1.06 1.06l-1.72-1.72V7.5h-1.5V4.06L9.53 5.78a.75.75 0 01-1.06-1.06l3-3zM11.25 7.5V15a.75.75 0 001.5 0V7.5h3.75a3 3 0 013 3v5.25a3 3 0 01-3 3H7.5a3 3 0 01-3-3V10.5a3 3 0 013-3h3.75z',
		message: __( 'The Roadmap board will be displayed here on the frontend. Plan and share your product roadmap from the admin panel.', 'changeloger' ),
		linkText: __( 'Manage Roadmap', 'changeloger' ),
	},
};

registerBlockType( 'cha/release-hub', {
	edit: function Edit( { attributes, setAttributes } ) {
		const {
			productId,
			showReleases,
			showFeedback,
			showRoadmap,
			defaultTab,
			releasesPerPage,
			releasesLabel,
			feedbackLabel,
			roadmapLabel,
			showHeader,
			showBadgeFilter,
			filterType,
			showSearch,
			layout,
			releasesWidthMode,
			releasesCustomWidth,
			feedbackWidthMode,
			feedbackCustomWidth,
			roadmapWidthMode,
			roadmapCustomWidth,
		} = attributes;
		const [ products, setProducts ] = useState( [] );
		const [ loading, setLoading ] = useState( true );
		const [ activeTab, setActiveTab ] = useState( defaultTab || 'releases' );

		// Premium status (localized by the free plugin) gates the Pro layouts.
		const isPremium = !! ( window.chaReleaseHubAdmin && window.chaReleaseHubAdmin.isPremium );

		const blockProps = useBlockProps( {
			className: 'cha-release-hub-editor',
		} );

		// Sync activeTab when defaultTab attribute changes.
		useEffect( () => {
			setActiveTab( defaultTab || 'releases' );
		}, [ defaultTab ] );

		// Ensure activeTab is valid when visibility toggles change.
		useEffect( () => {
			const visibleTabs = [];
			if ( showReleases ) {
				visibleTabs.push( 'releases' );
			}
			if ( showFeedback ) {
				visibleTabs.push( 'feedback' );
			}
			if ( showRoadmap ) {
				visibleTabs.push( 'roadmap' );
			}
			if ( visibleTabs.length > 0 && ! visibleTabs.includes( activeTab ) ) {
				setActiveTab( visibleTabs[ 0 ] );
			}
		}, [ showReleases, showFeedback, showRoadmap ] );

		// Fetch products on mount.
		useEffect( () => {
			apiFetch( { path: '/changeloger/v1/products' } )
				.then( ( data ) => {
					setProducts( data || [] );
					setLoading( false );
				} )
				.catch( () => {
					setLoading( false );
				} );
		}, [] );

		const productOptions = [
			{
				label: __( '— Select a product —', 'changeloger' ),
				value: 0,
			},
			...products.map( ( p ) => ( { label: p.name, value: p.id } ) ),
		];

		const selectedProduct = products.find( ( p ) => p.id === productId );

		// Build default tab options based on visible sections.
		const defaultTabOptions = [];
		if ( showReleases ) {
			defaultTabOptions.push( {
				label: releasesLabel || __( 'Releases', 'changeloger' ),
				value: 'releases',
			} );
		}
		if ( showFeedback ) {
			defaultTabOptions.push( {
				label: feedbackLabel || __( 'Feedback', 'changeloger' ),
				value: 'feedback',
			} );
		}
		if ( showRoadmap ) {
			defaultTabOptions.push( {
				label: roadmapLabel || __( 'Roadmap', 'changeloger' ),
				value: 'roadmap',
			} );
		}

		// Display labels (fall back to defaults).
		const displayReleasesLabel =
			releasesLabel || __( 'Releases', 'changeloger' );
		const displayFeedbackLabel =
			feedbackLabel || __( 'Feedback', 'changeloger' );
		const displayRoadmapLabel =
			roadmapLabel || __( 'Roadmap', 'changeloger' );

		return (
			<>
				<InspectorControls>
					{ /* Product Filter Panel */ }
					<PanelBody
						title={ __( 'Product Filter', 'changeloger' ) }
						initialOpen={ true }
					>
						{ loading ? (
							<Spinner />
						) : (
							<SelectControl
								label={ __(
									'Select Product',
									'changeloger'
								) }
								value={ productId }
								options={ productOptions }
								onChange={ ( value ) =>
									setAttributes( {
										productId: parseInt( value, 10 ),
									} )
								}
								help={
									productId === 0
										? __(
												'Please select a product to display releases.',
												'changeloger'
										  )
										: __(
												'Shows releases from this product.',
												'changeloger'
										  )
								}
							/>
						) }
					</PanelBody>

					{ /* Section Visibility Panel */ }
					<PanelBody
						title={ __( 'Section Visibility', 'changeloger' ) }
						initialOpen={ true }
					>
						<ToggleControl
							label={ __( 'Releases', 'changeloger' ) }
							help={
								showReleases
									? __(
											'Releases tab is visible.',
											'changeloger'
									  )
									: __(
											'Releases tab is hidden.',
											'changeloger'
									  )
							}
							checked={ showReleases }
							onChange={ ( value ) =>
								setAttributes( { showReleases: value } )
							}
						/>
						<ToggleControl
							label={ __( 'Feedback', 'changeloger' ) }
							help={
								showFeedback
									? __(
											'Feedback tab is visible.',
											'changeloger'
									  )
									: __(
											'Feedback tab is hidden.',
											'changeloger'
									  )
							}
							checked={ showFeedback }
							onChange={ ( value ) =>
								setAttributes( { showFeedback: value } )
							}
						/>
						<ToggleControl
							label={ __( 'Roadmap', 'changeloger' ) }
							help={
								showRoadmap
									? __(
											'Roadmap tab is visible.',
											'changeloger'
									  )
									: __(
											'Roadmap tab is hidden.',
											'changeloger'
									  )
							}
							checked={ showRoadmap }
							onChange={ ( value ) =>
								setAttributes( { showRoadmap: value } )
							}
						/>
					</PanelBody>

					{ /* Tab Settings Panel */ }
					<PanelBody
						title={ __( 'Tab Settings', 'changeloger' ) }
						initialOpen={ false }
					>
						{ defaultTabOptions.length > 1 && (
							<SelectControl
								label={ __(
									'Default Active Tab',
									'changeloger'
								) }
								value={ defaultTab }
								options={ defaultTabOptions }
								onChange={ ( value ) =>
									setAttributes( { defaultTab: value } )
								}
								help={ __(
									'The tab shown by default when the page loads.',
									'changeloger'
								) }
							/>
						) }
						{ showReleases && (
							<TextControl
								label={ __(
									'Releases Tab Label',
									'changeloger'
								) }
								value={ releasesLabel }
								onChange={ ( value ) =>
									setAttributes( { releasesLabel: value } )
								}
								placeholder={ __(
									'Releases',
									'changeloger'
								) }
								help={ __(
									'Custom label for the Releases tab.',
									'changeloger'
								) }
							/>
						) }
						{ showFeedback && (
							<TextControl
								label={ __(
									'Feedback Tab Label',
									'changeloger'
								) }
								value={ feedbackLabel }
								onChange={ ( value ) =>
									setAttributes( { feedbackLabel: value } )
								}
								placeholder={ __(
									'Feedback',
									'changeloger'
								) }
								help={ __(
									'Custom label for the Feedback tab.',
									'changeloger'
								) }
							/>
						) }
						{ showRoadmap && (
							<TextControl
								label={ __(
									'Roadmap Tab Label',
									'changeloger'
								) }
								value={ roadmapLabel }
								onChange={ ( value ) =>
									setAttributes( { roadmapLabel: value } )
								}
								placeholder={ __(
									'Roadmap',
									'changeloger'
								) }
								help={ __(
									'Custom label for the Roadmap tab.',
									'changeloger'
								) }
							/>
						) }
					</PanelBody>

					{ /* Releases Settings Panel */ }
					{ showReleases && (
						<PanelBody
							title={ __(
								'Releases Settings',
								'changeloger'
							) }
							initialOpen={ false }
						>
							<RangeControl
								label={ __(
									'Releases Per Page',
									'changeloger'
								) }
								value={ releasesPerPage }
								onChange={ ( value ) =>
									setAttributes( {
										releasesPerPage: value,
									} )
								}
								min={ 0 }
								max={ 50 }
								help={ __(
									'Number of releases to display. Set to 0 to use the global setting.',
									'changeloger'
								) }
							/>
							<SelectControl
								label={ __(
									'Layout Style',
									'changeloger'
								) }
								value={ layout }
								options={ [
									{
										label: __(
											'Inherit from settings',
											'changeloger'
										),
										value: 'global',
									},
									{
										label: __(
											'Stack',
											'changeloger'
										),
										value: 'default',
									},
									{
										label: __(
											'Compact',
											'changeloger'
										),
										value: 'compact',
									},
									{
										label: isPremium
											? __( 'Rail', 'changeloger' )
											: __( 'Rail (Pro)', 'changeloger' ),
										value: 'rail',
										disabled: ! isPremium,
									},
									{
										label: isPremium
											? __( 'Ticker', 'changeloger' )
											: __( 'Ticker (Pro)', 'changeloger' ),
										value: 'ticker',
										disabled: ! isPremium,
									},
									{
										label: isPremium
											? __( 'Mosaic', 'changeloger' )
											: __( 'Mosaic (Pro)', 'changeloger' ),
										value: 'mosaic',
										disabled: ! isPremium,
									},
								] }
								onChange={ ( value ) =>
									setAttributes( { layout: value } )
								}
								help={ __(
									'Inherit uses the global layout from Settings → Releases. Stack and Compact are free; Rail, Ticker, and Mosaic require Changeloger Pro.',
									'changeloger'
								) }
							/>
							<ToggleControl
								label={ __(
									'Show Subscribe Header',
									'changeloger'
								) }
								help={
									showHeader
										? __(
												'Subscribe header is visible.',
												'changeloger'
										  )
										: __(
												'Subscribe header is hidden.',
												'changeloger'
										  )
								}
								checked={ showHeader }
								onChange={ ( value ) =>
									setAttributes( { showHeader: value } )
								}
							/>
							<ToggleControl
								label={ __(
									'Show Filter',
									'changeloger'
								) }
								help={
									showBadgeFilter
										? __(
												'Filter pills are visible above the releases.',
												'changeloger'
										  )
										: __(
												'Filter pills are hidden.',
												'changeloger'
										  )
								}
								checked={ showBadgeFilter }
								onChange={ ( value ) =>
									setAttributes( {
										showBadgeFilter: value,
									} )
								}
							/>
							{ showBadgeFilter && (
								<SelectControl
									label={ __(
										'Filter By',
										'changeloger'
									) }
									value={ filterType }
									options={ [
										{
											label: __(
												'Badges',
												'changeloger'
											),
											value: 'badge',
										},
										{
											label: __(
												'Products',
												'changeloger'
											),
											value: 'product',
										},
									] }
									onChange={ ( value ) =>
										setAttributes( {
											filterType: value,
										} )
									}
									help={
										filterType === 'product'
											? __(
													'Shows all products as filter pills. Clicking a product shows its releases. The single "Select Product" setting above is ignored in this mode.',
													'changeloger'
											  )
											: __(
													'Shows release badges (Major, Minor, etc.) as filter pills.',
													'changeloger'
											  )
									}
								/>
							) }
							<ToggleControl
								label={ __(
									'Show Search Bar',
									'changeloger'
								) }
								help={
									showSearch
										? __(
												'Search bar is visible. Searches by title, version number, and release date.',
												'changeloger'
										  )
										: __(
												'Search bar is hidden.',
												'changeloger'
										  )
								}
								checked={ showSearch }
								onChange={ ( value ) =>
									setAttributes( {
										showSearch: value,
									} )
								}
							/>
						</PanelBody>
					) }

					{ /* Container Width Panel */ }
					<PanelBody
						title={ __( 'Container Width', 'changeloger' ) }
						initialOpen={ false }
					>
						{ showReleases && (
							<>
								<SelectControl
									label={ __(
										'Releases Width',
										'changeloger'
									) }
									value={ releasesWidthMode }
									options={ [
										{
											label: __(
												'Default',
												'changeloger'
											),
											value: 'default',
										},
										{
											label: __(
												'Full Width',
												'changeloger'
											),
											value: 'full',
										},
										{
											label: __(
												'Custom',
												'changeloger'
											),
											value: 'custom',
										},
									] }
									onChange={ ( value ) =>
										setAttributes( {
											releasesWidthMode: value,
										} )
									}
								/>
								{ releasesWidthMode === 'custom' && (
									<RangeControl
										label={ __(
											'Custom Width (px)',
											'changeloger'
										) }
										value={ releasesCustomWidth }
										onChange={ ( value ) =>
											setAttributes( {
												releasesCustomWidth:
													value,
											} )
										}
										min={ 300 }
										max={ 1600 }
										step={ 10 }
									/>
								) }
							</>
						) }
						{ showFeedback && (
							<>
								<SelectControl
									label={ __(
										'Feedback Width',
										'changeloger'
									) }
									value={ feedbackWidthMode }
									options={ [
										{
											label: __(
												'Default',
												'changeloger'
											),
											value: 'default',
										},
										{
											label: __(
												'Full Width',
												'changeloger'
											),
											value: 'full',
										},
										{
											label: __(
												'Custom',
												'changeloger'
											),
											value: 'custom',
										},
									] }
									onChange={ ( value ) =>
										setAttributes( {
											feedbackWidthMode: value,
										} )
									}
								/>
								{ feedbackWidthMode === 'custom' && (
									<RangeControl
										label={ __(
											'Custom Width (px)',
											'changeloger'
										) }
										value={ feedbackCustomWidth }
										onChange={ ( value ) =>
											setAttributes( {
												feedbackCustomWidth:
													value,
											} )
										}
										min={ 300 }
										max={ 1600 }
										step={ 10 }
									/>
								) }
							</>
						) }
						{ showRoadmap && (
							<>
								<SelectControl
									label={ __(
										'Roadmap Width',
										'changeloger'
									) }
									value={ roadmapWidthMode }
									options={ [
										{
											label: __(
												'Default',
												'changeloger'
											),
											value: 'default',
										},
										{
											label: __(
												'Full Width',
												'changeloger'
											),
											value: 'full',
										},
										{
											label: __(
												'Custom',
												'changeloger'
											),
											value: 'custom',
										},
									] }
									onChange={ ( value ) =>
										setAttributes( {
											roadmapWidthMode: value,
										} )
									}
								/>
								{ roadmapWidthMode === 'custom' && (
									<RangeControl
										label={ __(
											'Custom Width (px)',
											'changeloger'
										) }
										value={ roadmapCustomWidth }
										onChange={ ( value ) =>
											setAttributes( {
												roadmapCustomWidth:
													value,
											} )
										}
										min={ 300 }
										max={ 1600 }
										step={ 10 }
									/>
								) }
							</>
						) }
					</PanelBody>
				</InspectorControls>
				<div { ...blockProps }>
					<div className="cha-hub-preview">
						{ /* Block Title & Product Info */ }
						<div className="cha-hub-title-row">
							<svg
								className="cha-hub-icon"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={ 1.5 }
									d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
								/>
							</svg>
							<span className="cha-hub-block-title">
								{ __( 'Release Hub', 'changeloger' ) }
								{ selectedProduct &&
									` — ${ selectedProduct.name }` }
							</span>
							<span className="cha-hub-layout-badge">
								{ ( {
									global: __( 'Inherit', 'changeloger' ),
									default: __( 'Stack', 'changeloger' ),
									compact: __( 'Compact', 'changeloger' ),
									rail: __( 'Rail', 'changeloger' ),
									ticker: __( 'Ticker', 'changeloger' ),
									mosaic: __( 'Mosaic', 'changeloger' ),
								}[ layout ] ) ||
									__( 'Inherit', 'changeloger' ) }
								{ releasesPerPage > 0 &&
									` · ${ releasesPerPage } ${
										releasesPerPage === 1
											? __( 'item', 'changeloger' )
											: __( 'items', 'changeloger' )
									}` }
							</span>
						</div>

						{ /* Interactive Tabs */ }
						<div className="cha-hub-tabs-preview">
							{ showReleases && (
								<button
									type="button"
									className={
										activeTab === 'releases'
											? 'active'
											: ''
									}
									onClick={ () =>
										setActiveTab( 'releases' )
									}
								>
									{ displayReleasesLabel }
								</button>
							) }
							{ showFeedback && (
								<button
									type="button"
									className={
										activeTab === 'feedback'
											? 'active'
											: ''
									}
									onClick={ () =>
										setActiveTab( 'feedback' )
									}
								>
									{ displayFeedbackLabel }
								</button>
							) }
							{ showRoadmap && (
								<button
									type="button"
									className={
										activeTab === 'roadmap'
											? 'active'
											: ''
									}
									onClick={ () =>
										setActiveTab( 'roadmap' )
									}
								>
									{ displayRoadmapLabel }
								</button>
							) }
						</div>

						{ /* Tab Panel Content with Notices */ }
						<div className="cha-hub-panels-preview">
							{ showReleases && activeTab === 'releases' && (
								<TabPanelNotice
									tab="releases"
									label={ displayReleasesLabel }
									{ ...TAB_CONFIG.releases }
								/>
							) }
							{ showFeedback && activeTab === 'feedback' && (
								<TabPanelNotice
									tab="feedback"
									label={ displayFeedbackLabel }
									{ ...TAB_CONFIG.feedback }
								/>
							) }
							{ showRoadmap && activeTab === 'roadmap' && (
								<TabPanelNotice
									tab="roadmap"
									label={ displayRoadmapLabel }
									{ ...TAB_CONFIG.roadmap }
								/>
							) }
						</div>
					</div>
				</div>
			</>
		);
	},
	save: function Save() {
		// Server-side rendered.
		return null;
	},
} );

